// src/redux/reducer/socialSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getUserProfile,
  getFollowers,
  getFollowing,
  getUserPosts,
  getUserPostsPaginated,
  followUser,
  unfollowUser,
  acceptFollow,
  removeFollower,
  deletePost,
  updateProfilePrivacy,
  uploadAvatarAndSave,
  getDiscoverUsers,
  followUserFromDiscover,
  followUserFromList,
  getUserSocialProfile,
  unfollowUserFromList,
} from "../actions";

const initial = {
  byId: {},
  loading: {
    profile: {},
    posts: {},
    avatar: {},
    lists: {},
    socialProfile: {}, // ✅ ADD THIS
  },
  error: {
    profile: {},
    posts: {},
    avatar: {},
    lists: {},
    socialProfile: {}, // ✅ keep this (you already have it)
  },
  optimistic: { prevFollowingStatus: {} },
};

const ensureNode = (s, uid) => {
  if (!s.byId[uid]) s.byId[uid] = {};
  return s.byId[uid];
};

const partitionFollowers = (rows = []) =>
  rows.reduce(
    (acc, r) => {
      if (!r) return acc;

      const st = String(r?.status || "none").toLowerCase();

      // backend may send "pending" for incoming requests
      if (st === "active") acc.active.push(r);
      else if (st === "requested" || st === "pending") acc.requested.push(r);
      else acc.none.push(r);

      return acc;
    },
    { active: [], requested: [], none: [] },
  );

// API inconsistency: sometimes `posts` is returned as an array (often page 1)
// and sometimes as an object keyed by numeric indices (often page 2+).
// Normalize here so UI components can reliably treat it as an array.
const normalizePostsArray = (posts) => {
  if (!posts) return [];
  if (Array.isArray(posts)) return posts;
  if (typeof posts === "object") return Object.values(posts);
  return [];
};

const dedupePosts = (posts) => {
  const seen = new Set();
  const out = [];
  for (const p of posts) {
    const key = p?.id ?? p?.post_id ?? JSON.stringify(p);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
};

const socialSlice = createSlice({
  name: "social",
  initialState: initial,
  reducers: {},
  extraReducers: (b) => {
    // ---------- AVATAR ----------
    b.addCase(uploadAvatarAndSave.pending, (s, { meta }) => {
      const uid = meta?.arg?.userId || s?.user?.user?.id; // best effort
      if (uid) s.loading.avatar[uid] = true;
    });

    b.addCase(uploadAvatarAndSave.fulfilled, (s, { payload }) => {
      const { userId, avatar } = payload || {};
      if (userId && avatar) {
        const node = s.byId[userId] || {};
        const p = node.profile || {};
        s.byId[userId] = { ...node, profile: { ...p, avatar } };
        s.error.avatar[userId] = null;
        s.loading.avatar[userId] = false;
      }
    });

    b.addCase(uploadAvatarAndSave.rejected, (s, { payload, error, meta }) => {
      const uid = s?.user?.user?.id || meta?.arg?.userId;
      if (uid) {
        s.loading.avatar[uid] = false;
        s.error.avatar[uid] =
          (payload && payload.message) ||
          error?.message ||
          "Errore upload avatar";
      }
    });

    // ---------- PROFILE ----------

    // ---------- SOCIAL PROFILE (mutual follow, basic profile info) ----------
    b.addCase(getUserSocialProfile.pending, (s, { meta }) => {
      const uid = meta.arg;
      s.loading.socialProfile[uid] = true;
      s.error.socialProfile[uid] = null;
    });

    b.addCase(getUserSocialProfile.fulfilled, (s, { payload }) => {
      const { userId, data } = payload || {};
      const node = ensureNode(s, userId);
      node.socialProfile = data; // ✅ store separately (do NOT overwrite node.profile)
      s.loading.socialProfile[userId] = false;
      s.error.socialProfile[userId] = null;
    });

    b.addCase(getUserSocialProfile.rejected, (s, { meta, error }) => {
      const uid = meta.arg;
      s.loading.socialProfile[uid] = false;
      s.error.socialProfile[uid] =
        error?.message || "Errore caricamento social profile";
    });

    b.addCase(getUserProfile.pending, (s, { meta }) => {
      const uid = meta.arg;
      s.loading.profile[uid] = true;
      s.error.profile[uid] = null;
    });
    b.addCase(getUserProfile.fulfilled, (s, { payload }) => {
      const { userId, data } = payload;
      s.byId[userId] = { ...(s.byId[userId] || {}), profile: data };
      s.loading.profile[userId] = false;
      s.error.profile[userId] = null;
    });
    b.addCase(getUserProfile.rejected, (s, { meta, error }) => {
      const uid = meta.arg;
      s.loading.profile[uid] = false;
      s.error.profile[uid] = error?.message || "Errore caricamento profilo";
    });

    // ----- FOLLOWERS with pagination -----
    b.addCase(getFollowers.pending, (s, { meta }) => {
      const { userId, page } = meta.arg;
      const key = `${userId}:followers`;
      s.loading.lists[key] = true;
      s.error.lists[key] = null;

      // Clear data only on the first page (which the page-level effect
      // dispatches whenever the search query changes). Paginated fetches
      // (page >= 2) must keep prior items so they can be appended in
      // fulfilled — even when a search is active.
      if (page === 1) {
        const node = ensureNode(s, userId);
        node.followersList = { items: [], pagination: null };
        node.followersActive = [];
        node.followersRequested = [];
      }
    });

    b.addCase(getFollowers.fulfilled, (s, { payload }) => {
      const { userId, data, pagination, page, search } = payload;
      const node = ensureNode(s, userId);

      // Process followers data
      const parts = partitionFollowers(data);
      node.followersActive = parts.active;
      node.followersRequested = parts.requested;

      // Store paginated list for UI
      if (!node.followersList) {
        node.followersList = { items: [], pagination: null };
      }

      const allFollowers = [...parts.active, ...parts.requested, ...parts.none];

      if (page === 1) {
        // Replace items for first page (also the case for a new search,
        // because ContactsTab refetches with page=1 whenever the query
        // changes).
        node.followersList.items = allFollowers;
      } else {
        // Append items for subsequent pages
        const existingIds = new Set(
          node.followersList.items
            .map((f) => {
              const user = f.follower || f;
              return user?.id;
            })
            .filter((id) => id != null),
        );

        const newItems = allFollowers.filter((f) => {
          const user = f.follower || f;
          return user?.id && !existingIds.has(user.id);
        });

        node.followersList.items = [...node.followersList.items, ...newItems];
      }

      node.followersList.pagination = pagination;
      node.followersList.lastSearch = search;

      const key = `${userId}:followers`;
      s.loading.lists[key] = false;
      s.error.lists[key] = null;
    });

    b.addCase(getFollowers.rejected, (s, { meta, error }) => {
      const { userId } = meta.arg;
      const key = `${userId}:followers`;
      s.loading.lists[key] = false;
      s.error.lists[key] = error?.message || "Errore caricamento follower";
    });

    // ----- FOLLOWING with pagination -----
    b.addCase(getFollowing.pending, (s, { meta }) => {
      const { userId, page } = meta.arg;
      const key = `${userId}:following`;
      s.loading.lists[key] = true;
      s.error.lists[key] = null;

      // Clear data only on the first page (search changes always refetch
      // page=1, so this covers new-search resets too).
      if (page === 1) {
        const node = ensureNode(s, userId);
        node.followingList = { items: [], pagination: null };
        node.following = [];
      }
    });

    b.addCase(getFollowing.fulfilled, (s, { payload }) => {
      const { userId, data, pagination, page, search } = payload;
      const node = ensureNode(s, userId);

      // Defensive dedup by followed user id — guards against the API returning
      // the same followed user more than once (e.g. an over-broad search),
      // which previously let a user show up multiple times in Seguiti.
      const dedupeFollowing = (list) => {
        const seen = new Set();
        return (list || []).filter((f) => {
          const id = (f?.followed || f)?.id;
          if (id == null) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      };
      const deduped = dedupeFollowing(data);

      // Store in following array
      node.following = deduped;

      // Store paginated list for UI
      if (!node.followingList) {
        node.followingList = { items: [], pagination: null };
      }

      if (page === 1) {
        // Replace items for first page (also the case for a new search,
        // since ContactsTab refetches with page=1 on every query change).
        node.followingList.items = deduped;
      } else {
        // Append items for subsequent pages
        const existingIds = new Set(
          node.followingList.items
            .map((f) => {
              const user = f.followed || f;
              return user?.id;
            })
            .filter((id) => id != null),
        );

        const newItems = deduped.filter((f) => {
          const user = f.followed || f;
          return user?.id && !existingIds.has(user.id);
        });

        node.followingList.items = [...node.followingList.items, ...newItems];
      }

      node.followingList.pagination = pagination;
      node.followingList.lastSearch = search;

      const key = `${userId}:following`;
      s.loading.lists[key] = false;
      s.error.lists[key] = null;
    });

    b.addCase(getFollowing.rejected, (s, { meta, error }) => {
      const { userId } = meta.arg;
      const key = `${userId}:following`;
      s.loading.lists[key] = false;
      s.error.lists[key] = error?.message || "Errore caricamento seguiti";
    });

    // ----- FOLLOW state transitions on viewing someone else's profile -----
    b.addCase(followUser.pending, (s, { meta }) => {
      const uid = meta.arg;
      const node = ensureNode(s, uid);
      const p = node.profile || {};
      // store previous to rollback
      s.optimistic.prevFollowingStatus[uid] = p.following_status || "none";
      const isPublic = !!p.is_public_profile;
      node.profile = {
        ...p,
        following_status: isPublic ? "active" : "requested",
      };
      if (isPublic) node.profile.follower_count = (p.follower_count || 0) + 1;
    });

    b.addCase(followUser.fulfilled, (s, { payload }) => {
      const { userId, isPublic } = payload;
      const node = ensureNode(s, userId);
      const p = node.profile || {};
      node.profile = {
        ...p,
        following_status: isPublic ? "active" : "requested",
      };
      delete s.optimistic.prevFollowingStatus[userId];
    });

    b.addCase(followUser.rejected, (s, { meta }) => {
      const uid = meta.arg;
      const node = ensureNode(s, uid);
      const p = node.profile || {};
      const prev = s.optimistic.prevFollowingStatus[uid] || "none";
      const wasPublic = !!p.is_public_profile;
      node.profile = { ...p, following_status: prev };
      if (wasPublic && prev !== "active") {
        // we optimistically +1 only for public; if we rolled back to not-active, undo it
        node.profile.follower_count = Math.max(0, (p.follower_count || 0) - 1);
      }
      delete s.optimistic.prevFollowingStatus[uid];
    });

    b.addCase(unfollowUser.pending, (s, { meta }) => {
      const uid = meta.arg;
      const node = ensureNode(s, uid);
      const p = node.profile || {};
      s.optimistic.prevFollowingStatus[uid] = p.following_status || "none";
      const wasActive = p.following_status === "active";
      node.profile = { ...p, following_status: "none" };
      if (wasActive) {
        node.profile.follower_count = Math.max(0, (p.follower_count || 0) - 1);
      }
    });

    b.addCase(unfollowUser.fulfilled, (s, { payload }) => {
      const { userId, viewerId } = payload;
      const node = ensureNode(s, userId);
      const p = node.profile || {};
      node.profile = { ...p, following_status: "none" };
      node.profile.follower_count = Math.max(0, (p.follower_count || 0) - 1);
      delete s.optimistic.prevFollowingStatus[userId];

      // ✅ Remove the unfollowed target from *viewer's* following list
      if (viewerId && s.byId[viewerId]?.following) {
        const list = s.byId[viewerId].following || [];
        s.byId[viewerId].following = list.filter((it) => {
          const fid = it?.followed?.id ?? it?.following?.id ?? it?.id ?? null;
          return String(fid) !== String(userId);
        });
      }

      // Also remove from followingList
      if (viewerId && s.byId[viewerId]?.followingList?.items) {
        const list = s.byId[viewerId].followingList.items || [];
        s.byId[viewerId].followingList.items = list.filter((it) => {
          const fid = it?.followed?.id ?? it?.following?.id ?? it?.id ?? null;
          return String(fid) !== String(userId);
        });
      }
    });

    b.addCase(unfollowUser.rejected, (s, { meta }) => {
      const uid = meta.arg;
      const node = ensureNode(s, uid);
      const p = node.profile || {};
      const prev = s.optimistic.prevFollowingStatus[uid] || "none";
      node.profile = { ...p, following_status: prev };
      if (prev === "active") {
        node.profile.follower_count = (p.follower_count || 0) + 1;
      }
      delete s.optimistic.prevFollowingStatus[uid];
    });

    // Owner actions on incoming requests / followers
    b.addCase(acceptFollow.fulfilled, (s, { payload }) => {
      const { followerId, ownerId } = payload;
      const node = ensureNode(s, ownerId);
      const req = (node.followersRequested || []).filter(
        (r) => r?.follower?.id !== followerId,
      );
      const moved = (node.followersRequested || []).find(
        (r) => r?.follower?.id === followerId,
      );
      node.followersRequested = req;
      if (!node.followersActive) node.followersActive = [];
      if (moved) node.followersActive = [moved, ...node.followersActive];
      // bump counter on my profile, if present
      const p = node.profile;
      if (p) p.follower_count = (p.follower_count || 0) + 1;

      // Update followersList
      if (node.followersList?.items) {
        node.followersList.items = node.followersList.items.map((item) =>
          item.follower?.id === followerId
            ? { ...item, status: "active" }
            : item,
        );
      }
    });

    b.addCase(removeFollower.fulfilled, (s, { payload }) => {
      const { followerId, ownerId } = payload;
      const node = ensureNode(s, ownerId);
      const beforeActive = node.followersActive?.length || 0;
      node.followersActive = (node.followersActive || []).filter(
        (r) => r?.follower?.id !== followerId,
      );
      node.followersRequested = (node.followersRequested || []).filter(
        (r) => r?.follower?.id !== followerId,
      );
      // decrement if it was in active
      if ((node.followersActive?.length || 0) < beforeActive && node.profile) {
        node.profile.follower_count = Math.max(
          0,
          (node.profile.follower_count || 0) - 1,
        );
      }

      // Update followersList
      if (node.followersList?.items) {
        node.followersList.items = node.followersList.items.filter(
          (item) => item.follower?.id !== followerId,
        );
      }
    });

    // ---------- POSTS ----------
    b.addCase(getUserPosts.pending, (s, { meta }) => {
      const uid = meta.arg;
      s.loading.posts[uid] = true;
      s.error.posts[uid] = null;
    });
    b.addCase(getUserPosts.fulfilled, (s, { payload }) => {
      const { userId, data } = payload;
      s.byId[userId] = {
        ...(s.byId[userId] || {}),
        posts:
          data && typeof data === "object" && "posts" in data
            ? { ...data, posts: normalizePostsArray(data.posts) }
            : data,
        isPrivateForViewer: false,
      };
      s.loading.posts[userId] = false;
      s.error.posts[userId] = null;
    });
    b.addCase(getUserPosts.rejected, (s, { payload, meta, error }) => {
      const uid = (payload && payload.userId) || meta.arg;
      if (payload?.private && uid) {
        s.byId[uid] = {
          ...(s.byId[uid] || {}),
          posts: [],
          isPrivateForViewer: true,
        };
        s.error.posts[uid] = null; // private is not an error state
      } else {
        s.error.posts[uid] = error?.message || "Errore caricamento post";
      }
      s.loading.posts[uid] = false;
    });

    // ----- GET USER POSTS PAGINATED (append to existing) -----
    b.addCase(getUserPostsPaginated.pending, (s, { meta }) => {
      const { userId, page, silent } = meta.arg;
      // Show loading for the initial page, but avoid flicker for appended pages
      // and for the quiet refresh while a video post is processing.
      if (page === 1 && !silent) {
        s.loading.posts[userId] = true;
      }
      s.error.posts[userId] = null;
    });
    b.addCase(getUserPostsPaginated.fulfilled, (s, { payload }) => {
      const { userId, data, page } = payload;
      const node = ensureNode(s, userId);

      const normalized =
        data && typeof data === "object" && "posts" in data
          ? { ...data, posts: normalizePostsArray(data.posts) }
          : data;
      
      if (page === 1) {
        // First page - replace
        node.posts = normalized;
      } else {
        // Subsequent pages - append posts
        const existingPosts = normalizePostsArray(node.posts?.posts);
        const newPosts = normalizePostsArray(normalized?.posts);

        node.posts =
          normalized && typeof normalized === "object" && "posts" in normalized
            ? {
                ...normalized,
                posts: dedupePosts([...existingPosts, ...newPosts]),
              }
            : normalized;
      }
      node.isPrivateForViewer = false;
      s.loading.posts[userId] = false;
      s.error.posts[userId] = null;
    });
    b.addCase(getUserPostsPaginated.rejected, (s, { payload, meta, error }) => {
      const { userId } = meta.arg;
      if (payload?.private && userId) {
        s.byId[userId] = {
          ...(s.byId[userId] || {}),
          posts: [],
          isPrivateForViewer: true,
        };
        s.error.posts[userId] = null;
      } else {
        s.error.posts[userId] = error?.message || "Errore caricamento post";
      }
      s.loading.posts[userId] = false;
    });

    // ----- DELETE POST -----
    b.addCase(deletePost.fulfilled, (s, { payload }) => {
      const { myId, postGroupId } = payload || {};
      if (!myId || !postGroupId) return;
      const node = ensureNode(s, myId);

      // NEW: support new payload shape { posts: [...] }
      if (Array.isArray(node.posts)) {
        node.posts = node.posts.filter((p) => p?.id !== postGroupId);
      } else if (node.posts?.posts) {
        node.posts.posts = (node.posts.posts || []).filter(
          (p) => p?.id !== postGroupId,
        );
      }

      if (node.profile) {
        node.profile.post_count = Math.max(
          0,
          +(node.profile.post_count || 0) - 1,
        );
      }
    });

    // ---------- PRIVACY ----------
    b.addCase(updateProfilePrivacy.fulfilled, (s, { payload }) => {
      const { myUserId, is_public_profile } = payload;
      const p = s.byId[myUserId]?.profile;
      if (p) p.is_public_profile = is_public_profile;
    });

    // ----- DISCOVER USERS -----
    b.addCase(getDiscoverUsers.pending, (s, { meta }) => {
      const { userId, page } = meta.arg;
      const key = `${userId}:discover`;
      s.loading.lists[key] = true;
      s.error.lists[key] = null;

      // Clear data only on the first page (search changes always refetch
      // page=1, so this covers new-search resets too).
      if (page === 1) {
        const node = ensureNode(s, userId);
        node.discoverUsers = { items: [], pagination: null };
      }
    });

    b.addCase(getDiscoverUsers.fulfilled, (s, { payload }) => {
      const { userId, data, page, search } = payload;
      const node = ensureNode(s, userId);

      if (!node.discoverUsers) {
        node.discoverUsers = { items: [], pagination: null };
      }

      // Handle API response format - adjust based on your actual API response
      const users = data.users || data.data || [];
      const pagination = data.pagination || {
        current_page: page,
        last_page: 1,
        per_page: 10,
        total: users.length,
        hasMore: false,
      };

      if (page === 1) {
        // Replace items for first page (also the case for a new search,
        // since ContactsTab refetches with page=1 on every query change).
        node.discoverUsers.items = users;
      } else {
        // Append items for subsequent pages
        const existingIds = new Set(node.discoverUsers.items.map((u) => u.id));
        const newItems = users.filter((u) => !existingIds.has(u.id));
        node.discoverUsers.items = [...node.discoverUsers.items, ...newItems];
      }

      node.discoverUsers.pagination = pagination;
      node.discoverUsers.lastSearch = search;

      const key = `${userId}:discover`;
      s.loading.lists[key] = false;
      s.error.lists[key] = null;
    });

    b.addCase(getDiscoverUsers.rejected, (s, { meta, error, payload }) => {
      const { userId } = meta.arg;
      const key = `${userId}:discover`;
      s.loading.lists[key] = false;
      s.error.lists[key] =
        payload?.message || error?.message || "Errore caricamento utenti";
    });

    // ----- FOLLOW FROM DISCOVER -----
    b.addCase(followUserFromDiscover.pending, (s, { meta }) => {
      const { targetUserId, userId } = meta.arg;
      const node = ensureNode(s, userId);
      if (node.discoverUsers?.items) {
        // Update follow status optimistically
        node.discoverUsers.items = node.discoverUsers.items.map((user) =>
          user.id === targetUserId
            ? {
                ...user,
                following_status: "requested",
                isFollowing: true,
                // Keep other properties
                name: user.name,
                nickname: user.nickname,
                age: user.age,
                city: user.city,
                goals: user.goals,
                interests: user.interests,
              }
            : user,
        );
      }
    });

    b.addCase(followUserFromDiscover.fulfilled, (s, { payload }) => {
      const { userId, targetUserId, followingStatus } = payload;
      const node = ensureNode(s, userId);
      if (node.discoverUsers?.items) {
        node.discoverUsers.items = node.discoverUsers.items.map((user) =>
          user.id === targetUserId
            ? {
                ...user,
                following_status: followingStatus,
                isFollowing: followingStatus !== "none",
              }
            : user,
        );
      }
    });

    b.addCase(followUserFromDiscover.rejected, (s, { meta, error }) => {
      const { userId, targetUserId } = meta.arg;
      const node = ensureNode(s, userId);
      if (node.discoverUsers?.items) {
        // Rollback optimistic update
        node.discoverUsers.items = node.discoverUsers.items.map((user) =>
          user.id === targetUserId
            ? { ...user, following_status: "none", isFollowing: false }
            : user,
        );
      }
    });

    // ----- FOLLOW FROM LIST -----
    b.addCase(followUserFromList.pending, (s, { meta }) => {
      const { targetUserId, viewerId, listType } = meta.arg;

      if (listType === "followers" && viewerId) {
        const node = ensureNode(s, viewerId);
        // Update followersList
        if (node.followersList?.items) {
          node.followersList.items = node.followersList.items.map(
            (follower) => {
              const user = follower.follower || follower;
              if (user?.id === targetUserId) {
                return {
                  ...follower,
                  follower: {
                    ...user,
                    following_status: "requested",
                    isFollowing: true,
                  },
                };
              }
              return follower;
            },
          );
        }
        // Also update followersActive
        if (node.followersActive) {
          node.followersActive = node.followersActive.map((follower) =>
            follower.follower?.id === targetUserId
              ? {
                  ...follower,
                  follower: {
                    ...follower.follower,
                    following_status: "requested",
                    isFollowing: true,
                  },
                }
              : follower,
          );
        }
      }
    });

    b.addCase(followUserFromList.fulfilled, (s, { payload }) => {
      const { targetUserId, viewerId, listType, followingStatus } = payload;

      if (listType === "followers" && viewerId) {
        const node = ensureNode(s, viewerId);
        // Update followersList
        if (node.followersList?.items) {
          node.followersList.items = node.followersList.items.map(
            (follower) => {
              const user = follower.follower || follower;
              if (user?.id === targetUserId) {
                return {
                  ...follower,
                  follower: {
                    ...user,
                    following_status: followingStatus,
                    isFollowing: followingStatus !== "none",
                  },
                };
              }
              return follower;
            },
          );
        }
        // Also update followersActive
        if (node.followersActive) {
          node.followersActive = node.followersActive.map((follower) =>
            follower.follower?.id === targetUserId
              ? {
                  ...follower,
                  follower: {
                    ...follower.follower,
                    following_status: followingStatus,
                    isFollowing: followingStatus !== "none",
                  },
                }
              : follower,
          );
        }
      }

      // Also update following list if needed
      if (listType === "following" && viewerId) {
        const node = ensureNode(s, viewerId);
        if (node.followingList?.items) {
          node.followingList.items = node.followingList.items.map(
            (following) => {
              const user = following.followed || following;
              if (user?.id === targetUserId) {
                return {
                  ...following,
                  following_status: followingStatus,
                  isFollowing: followingStatus !== "none",
                };
              }
              return following;
            },
          );
        }
      }
    });

    b.addCase(followUserFromList.rejected, (s, { meta, error }) => {
      const { targetUserId, viewerId, listType } = meta.arg;

      // Rollback optimistic update
      if (listType === "followers" && viewerId) {
        const node = ensureNode(s, viewerId);
        if (node.followersList?.items) {
          node.followersList.items = node.followersList.items.map(
            (follower) => {
              const user = follower.follower || follower;
              if (user?.id === targetUserId) {
                return {
                  ...follower,
                  follower: {
                    ...user,
                    following_status: "none",
                    isFollowing: false,
                  },
                };
              }
              return follower;
            },
          );
        }
        if (node.followersActive) {
          node.followersActive = node.followersActive.map((follower) =>
            follower.follower?.id === targetUserId
              ? {
                  ...follower,
                  follower: {
                    ...follower.follower,
                    following_status: "none",
                    isFollowing: false,
                  },
                }
              : follower,
          );
        }
      }
    });

    // ----- UNFOLLOW FROM LIST -----
    b.addCase(unfollowUserFromList.pending, (s, { meta }) => {
      const { targetUserId, viewerId, listType } = meta.arg;

      if (listType === "following" && viewerId) {
        const node = ensureNode(s, viewerId);
        if (node.followingList?.items) {
          node.followingList.items = node.followingList.items.map(
            (following) => {
              const user = following.followed || following;
              if (user?.id === targetUserId) {
                return {
                  ...following,
                  following_status: "none",
                  isFollowing: false,
                };
              }
              return following;
            },
          );
        }
      }
    });

    b.addCase(unfollowUserFromList.fulfilled, (s, { payload }) => {
      const { targetUserId, viewerId, listType, followingStatus } = payload;

      if (listType === "following" && viewerId) {
        const node = ensureNode(s, viewerId);
        if (node.followingList?.items) {
          node.followingList.items = node.followingList.items.map(
            (following) => {
              const user = following.followed || following;
              if (user?.id === targetUserId) {
                return {
                  ...following,
                  following_status: followingStatus,
                  isFollowing: followingStatus !== "none",
                };
              }
              return following;
            },
          );
        }
      }
    });

    b.addCase(unfollowUserFromList.rejected, (s, { meta, error }) => {
      const { targetUserId, viewerId, listType } = meta.arg;

      // Rollback optimistic update for following list
      if (listType === "following" && viewerId) {
        const node = ensureNode(s, viewerId);
        if (node.followingList?.items) {
          node.followingList.items = node.followingList.items.map(
            (following) => {
              const user = following.followed || following;
              if (user?.id === targetUserId) {
                return {
                  ...following,
                  following_status: "active",
                  isFollowing: true,
                };
              }
              return following;
            },
          );
        }
      }
    });
  },
});

export default socialSlice.reducer;
